import React, { useState, useEffect, useCallback } from 'react';
import { useLocation } from 'react-router-dom';
import { cookieGetValue, cookieSetValue } from 'hooks/useCookie';
import campaigns from './campaignConfig';
import MarketingPopupEmbedForm from './MarketingPopupEmbedForm';
import MarketingPopupStaticContent from './MarketingPopupStaticContent';
import './MarketingPopup.scss';

const getCookieName = (campaignId) => `marketing_popup_${campaignId}`;

const isWithinDateRange = (campaign) => {
  const now = new Date();
  if (campaign.startDate && new Date(campaign.startDate) > now) return false;
  if (campaign.endDate && new Date(campaign.endDate) < now) return false;
  return true;
};

const getActiveCampaign = (pathname) => {
  return campaigns.find(
    (c) =>
      c.enabled &&
      isWithinDateRange(c) &&
      (!c.paths || c.paths.includes(pathname)) &&
      cookieGetValue(getCookieName(c.id)) !== 'dismissed'
  );
};

const MarketingPopup = () => {
  const { pathname } = useLocation();
  const [campaign, setCampaign] = useState(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isDismissing, setIsDismissing] = useState(false);

  useEffect(() => {
    const active = getActiveCampaign(pathname);
    if (!active) {
      setCampaign(null);
      setIsVisible(false);
      return;
    }

    setCampaign(active);

    const timer = setTimeout(() => {
      setIsVisible(true);
    }, active.delayMs || 2000);

    return () => clearTimeout(timer);
  }, [pathname]);

  const handleDismiss = useCallback(() => {
    if (!campaign) return;

    setIsDismissing(true);
    cookieSetValue(getCookieName(campaign.id), 'dismissed', {
      path: '/',
      maxAge: (campaign.cookieDurationDays || 30) * 86400
    });

    setTimeout(() => {
      setIsVisible(false);
      setIsDismissing(false);
      setCampaign(null);
    }, 300);
  }, [campaign]);

  if (!campaign || (!isVisible && !isDismissing)) return null;

  const popupClass = [
    'marketing-popup',
    isVisible && !isDismissing ? 'marketing-popup--visible' : '',
    isDismissing ? 'marketing-popup--dismissing' : ''
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <div className={popupClass}>
      <div className='marketing-popup__header'>
        <h3 className='marketing-popup__title'>{campaign.title}</h3>
        <button
          className='marketing-popup__close'
          onClick={handleDismiss}
          aria-label='Close popup'
        >
          &times;
        </button>
      </div>
      <div className='marketing-popup__body'>
        {campaign.description && (
          <p className='marketing-popup__description'>{campaign.description}</p>
        )}
        {campaign.type === 'embed_form' && (
          <MarketingPopupEmbedForm campaign={campaign} />
        )}
        {campaign.type === 'static' && (
          <MarketingPopupStaticContent campaign={campaign} />
        )}
        {campaign.privacyNotice && (
          <p className='marketing-popup__privacy'>{campaign.privacyNotice}</p>
        )}
      </div>
    </div>
  );
};

export default MarketingPopup;
